import { useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import './ReportBug.css';

export default function ReportBug() {
  const { user } = useAuth();
  const { submitBugReport } = useApi();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const reporter = useMemo(() => ({
    reporterEmail: user?.email || '',
    reporterName: user?.name || ''
  }), [user]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
  };

  const validateForm = () => {
    if (!title.trim()) {
      return 'Please add a short title for your issue.';
    }

    if (!description.trim()) {
      return 'Please describe the issue you ran into.';
    }

    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    const validationError = validateForm();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      await submitBugReport({
        title: title.trim(),
        description: description.trim(),
        reporterEmail: reporter.reporterEmail,
        reporterName: reporter.reporterName
      });
      setSuccessMessage("Thank you! Your bug report has been submitted. We'll look into it soon.");
      resetForm();
    } catch (error) {
      const fallbackMessage = 'Unable to submit your bug report. Please try again later.';
      setErrorMessage(error?.userMessage || error?.message || fallbackMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="report-bug-page">
      <div className="report-bug-content">
        <div className="report-bug-intro">
          <p>
            Something not working as expected? Let us know and we'll take a look.
          </p>
        </div>

        <div className="report-bug-card">
        <form className="report-bug-form" onSubmit={handleSubmit}>
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="bug-title">Title *</label>
            <input
              id="bug-title"
              name="title"
              type="text"
              className="form-control"
              placeholder="Brief description of the issue"
              maxLength={200}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="bug-description">Description *</label>
            <textarea
              id="bug-description"
              name="description"
              className="form-control"
              placeholder="Please describe what happened and what you expected to happen"
              rows={8}
              maxLength={2000}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={isSubmitting}
              required
            />
          </div>

          <div className="report-bug-actions">
            <button
              type="submit"
              className="primary-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit bug report'}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
